import useStore from "../../../../../../helpers/useStore";
import { TransactionsContext } from "../../../../../../stores/Transactions/Transactions.provider";
import notificationStore from "../../../../../../stores/Notification/notificationStore";
import { UpdateInsuranceTransactionDto } from "../../../../../../api-client";

/**
 * Hook for handling RX deletion logic
 */
export const useRxDeletion = () => {
  const TransactionStore = useStore(TransactionsContext);
  const { softDeleteTransaction } = TransactionStore;

  /**
   * Helper function to create DTO from bill
   */
  const createBillingDataDto = (
    bill: any,
    notes: Record<string, string>
  ): UpdateInsuranceTransactionDto => {
    const { payerIK, accountInfo, data, userInfo, wasExported } = bill;
    const { rxs, payerDto, totalPrice } = data;

    const rxsIds = rxs.map((rx: any) => rx.id).filter((id: any): id is string => !!id);
    const paperCenter = payerDto?.paperReceivingCenter?.ik;
    const digitalCenter = payerDto?.digitalReceivingCenter?.ik;

    return {
      payerId: payerIK,
      accountId: accountInfo.id,
      amount: totalPrice,
      userId: userInfo.id!,
      rxIds: rxsIds,
      paperCenterId: paperCenter || undefined,
      dataCenterId: digitalCenter || undefined,
      notes: notes[bill.billId] || "",
      wasExported,
      tAccountId: rxs[0].tarifGroupId === "Privat" ? 4105 : 4101,
    };
  };

  /**
   * Handle RX deletion with proper soft delete logic
   */
  const handleRxDeletion = async (
    selectedRx: any,
    bills: any[],
    setBills: React.Dispatch<React.SetStateAction<any[]>>,
    notes: Record<string, string>,
    onRxDelete: (rxId: string) => void
  ) => {
    if (!selectedRx) return;

    // Find the bill containing this RX
    const billWithRx = bills.find((bill: any) =>
      bill.data.rxs.some((rx: any) => rx.id === selectedRx.id)
    );

    if (!billWithRx) {
      notificationStore.showMessage("Rechnung nicht gefunden", "error");
      return;
    }

    // If this is the only RX in the bill, soft delete the entire bill
    if (billWithRx.data.rxs.length === 1) {
      try {
        await softDeleteTransaction(billWithRx.billId);

        // Remove the entire bill from the list
        setBills((prevBills: any[]) =>
          prevBills.filter((bill: any) => bill.billId !== billWithRx.billId)
        );

        notificationStore.showMessage("Rechnung erfolgreich gelöscht", "success");
        onRxDelete(selectedRx.id);
      } catch (error) {
        console.error("Error soft deleting transaction:", error);
        notificationStore.showMessage("Fehler beim Löschen der Rechnung", "error");
      }
    } else {
      // Remove the RX from the bill and update the bill
      setBills((prevBills: any[]) =>
        prevBills.map((bill: any) => {
          if (bill.data.rxs.some((rx: any) => rx.id === selectedRx.id)) {
            const updatedRxData = bill.data.rxs.filter((rx: any) => rx.id !== selectedRx.id);
            const updatedTotalPrice = updatedRxData.reduce(
              (total: number, rx: any) => total + (rx.rxPrice.netValue || 0),
              0
            );
            return {
              ...bill,
              data: {
                ...bill.data,
                rxs: updatedRxData,
                totalPrice: updatedTotalPrice,
              },
            };
          }
          return bill;
        })
      );

      notificationStore.showMessage("Rezept erfolgreich aus der Abrechnung entfernt", "success");
      onRxDelete(selectedRx.id);
    }
  };

  return {
    handleRxDeletion,
    createBillingDataDto,
  };
};
